/**
 * Pedido de redefinição de senha.
 *
 * A confirmação é deliberadamente ambígua: *"Se existir conta com este e-mail,
 * o link chegou lá"*. Dizer "enviamos" confirmaria que a conta existe, e esta
 * tela não exige senha nenhuma — qualquer pessoa poderia testar endereços à
 * vontade para descobrir quem usa o Movhuman.
 *
 * A tela de login diz "confirme seu e-mail" e esta não diz nada. A diferença é
 * que lá a pessoa já provou saber a senha; aqui, não provou nada.
 */

import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/Alert';
import { Brand } from '@/components/Brand';
import { authErrorKey } from '@/features/auth/authErrors';
import { requestPasswordReset } from '@/features/auth/useSession';

export default function ForgotPasswordPage() {
  const { t } = useTranslation(['auth', 'errors', 'common']);

  const [email, setEmail] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErrorKey(null);
    setBusy(true);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (error) {
      const key = authErrorKey(error);
      // Só o limite de envio e a falha de rede são reportados. Qualquer outro
      // erro vira "enviado" — informar que falhou já entregaria que a conta
      // não existe.
      if (key === 'auth:error.rateLimited' || key === 'errors:network_unreachable') {
        setErrorKey(key);
      } else {
        setSent(true);
      }
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <main className="mh-shell justify-center px-5 py-10">
        <div className="mh-card">
          <h1 className="text-2xl">{t('auth:forgot.sent', { email })}</h1>
          <p className="mt-2 text-(--color-sand-700)">{t('auth:forgot.sentHint')}</p>
        </div>
        <p className="mt-8 text-center">
          <Link to="/sign-in" className="text-sm font-semibold text-(--color-amber-700)">
            {t('auth:forgot.back')}
          </Link>
        </p>
      </main>
    );
  }

  return (
    <main className="mh-shell justify-center px-5 py-10">
      <Brand />
      <h1 className="text-3xl">{t('auth:forgot.title')}</h1>
      <p className="mt-1 mb-8 text-(--color-sand-700)">{t('auth:forgot.subtitle')}</p>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <div className="mh-field">
          <label htmlFor="email">{t('auth:signIn.email')}</label>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        {errorKey ? (
          <Alert tone="error" live="assertive">
            {t(errorKey)}
          </Alert>
        ) : null}

        <button type="submit" className="mh-button mh-button--primary mt-2" disabled={busy}>
          {busy ? t('common:state.loading') : t('auth:forgot.submit')}
        </button>
      </form>

      <p className="mt-8 text-center">
        <Link to="/sign-in" className="text-sm font-semibold text-(--color-amber-700)">
          {t('auth:forgot.back')}
        </Link>
      </p>
    </main>
  );
}
